import { api_configuration } from "../../common";
import DestructorTree from "../DestructorTree";
import {
	setState,
	setWidgetState,
	storeNumber,
	storeState,
	storeText,
	storeWidgetNumber,
	storeWidgetText,
} from "../state/modifications";
import { isDefined } from "../validation";
import ImplementationVisitorContext from "./ImplementationVisitorContext";
import { visitExpression } from "./visit_widget_implementation_expression";
import api_views = api_configuration.libraries.gui_definition_lib.types.views.alan_api;
import api_widget = api_configuration.libraries.gui_definition_lib.types.widget.alan_api;
import api_modification = api_configuration.types.modifications.alan_api;
import context_selection = require("./context_selection");
import { EventValue } from "./ExpressionTypes";
import { decorateInstructionError, isNonDecoratedError } from "./stackTraceMessage";
import { logInstruction } from "../state/ClientState";
import { InstructionError } from "./instructions/InstructionError";

type Writeable<T> = { -readonly [P in keyof T]: T[P] };

async function executeInstruction(
	event_name: string,
	implementation_context: ImplementationVisitorContext,
	argument_context: ImplementationVisitorContext,
	event_selection: api_widget.Cbinding__configuration_attribute_type,
	destructor: DestructorTree,
	event_value: EventValue
): Promise<void> {
	let typed_events = [];

	switch (event_selection.properties.has_arguments.state.name) {
		case "no":
			break;
		case "yes": {
			const mapArgument = async (argument: api_widget.Cinstruction_argument): Promise<any> => {
				switch (argument.properties.type.state.name) {
					case "file": {
						return event_value;
					}
					case "number": {
						const number_type = argument.properties.type.state.node;
						return await visitExpression(number_type.properties.argument.properties.expression).execute(
							argument_context,
							destructor,
							event_value
						);
					}
					case "text": {
						const text_type = argument.properties.type.state.node;
						return await visitExpression(text_type.properties.argument.properties.expression).execute(
							argument_context,
							destructor,
							event_value
						);
					}
					case "view": {
						const $ = argument.properties.type.state.node;
						const view_configuration_key = $.properties.view_configuration.entry;
						const view_argument_context = context_selection.context_selection(
							$.properties.context,
							argument_context,
							new DestructorTree()
						);
						// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
						const widget_configuration_view = view_argument_context.gui_widget_configuration.properties.configuration
							.get(view_configuration_key)!
							.properties.attribute.properties.type.cast("view")
							.properties.render.cast("in window");
						return widget_configuration_view;
					}
					default:
						unexpectedState(argument.properties.type.state);
				}
			};

			const mapArguments = async (argument: api_widget.Cinstruction_argument): Promise<any[]> => {
				const typed_argument = await mapArgument(argument);
				switch (argument.properties.next_argument.state.name) {
					case "exists": {
						const $ = argument.properties.next_argument.state.node;
						return [typed_argument].concat(await mapArguments($.properties.argument));
					}
					case "no": {
						return [typed_argument];
					}
					default:
						unexpectedState(argument.properties.next_argument.state);
				}
			};

			const has_argument = event_selection.properties.has_arguments.state.node;
			typed_events = await mapArguments(has_argument.properties.first_argument);
			break;
		}
		default:
			unexpectedState(event_selection.properties.has_arguments.state);
	}

	if (implementation_context.current_binding_context.destroyed) {
		console.warn(
			"trying to execute an instruction on a destroyed binding context:" +
				implementation_context.current_binding_context.path
		);
		return;
	}

	logInstruction(implementation_context.current_binding_context, event_name);

	return (
		isDefined(implementation_context.client_state_binder_AsPropertiesBinding.instruction_handlers)[event_name](
			implementation_context,
			...typed_events
		) || Promise.resolve()
	);
}

async function execute_configuration_intruction_handler(
	implementation_context: ImplementationVisitorContext,
	argument_context: ImplementationVisitorContext,
	configuration_instruction: api_widget.Cconfiguration__configuration_attribute_type,
	destructor: DestructorTree,
	value?: EventValue
): Promise<void> {
	const node_context = implementation_context.view_context_node;
	switch (configuration_instruction.properties.instruction.state.name) {
		case "set number": {
			const set_number = configuration_instruction.properties.instruction.state.node;
			const new_value = (await visitExpression(set_number.properties.argument.properties.expression).execute(
				argument_context,
				destructor,
				value
			)) as number;

			const widget_configuration = implementation_context.widget_configuration;
			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const number_configuration_property = widget_configuration.properties.configuration
					.get(set_number.properties.number.entry)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("number");
				if (number_configuration_property === undefined) {
					storeWidgetNumber(
						node_context,
						implementation_context.last_gui_widget_configuration,
						set_number.properties.number.ref,
						new_value,
						implementation_context.entry_keys
					);
				} else {
					storeNumber(node_context, number_configuration_property, new_value, implementation_context.entry_keys);
				}
			} else if (widget_configuration instanceof api_modification.Cconfiguration_node) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const number_property = widget_configuration.properties.attributes
					.get(set_number.properties.number.entry)!
					.properties.type.cast("number");
				number_property.set_value(new_value);
				storeState(widget_configuration.root);
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				storeWidgetNumber(
					node_context,
					implementation_context.last_gui_widget_configuration,
					set_number.properties.number.ref,
					new_value,
					implementation_context.entry_keys
				);
			} else {
				unexpectedState(widget_configuration);
			}
			break;
		}
		case "set state": {
			const set_state = configuration_instruction.properties.instruction.state.node;
			const widget_configuration = implementation_context.widget_configuration;
			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const state_configuration_property = widget_configuration.properties.configuration
					.get(set_state.properties.state_group.entry)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("state group");
				if (state_configuration_property === undefined) {
					setWidgetState(
						node_context,
						implementation_context.last_gui_widget_configuration,
						set_state.properties.state_group.ref,
						set_state.properties.state.entry,
						await mapInstructionArgumentToModification(
							set_state.properties.node,
							destructor,
							value,
							implementation_context
						),
						implementation_context.entry_keys
					);
				} else {
					setState(
						node_context,
						state_configuration_property,
						set_state.properties.state.entry,
						await mapInstructionArgumentToModification(
							set_state.properties.node,
							destructor,
							value,
							implementation_context
						),
						implementation_context.entry_keys
					);
				}
			} else if (widget_configuration instanceof api_modification.Cconfiguration_node) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const state_configuration_property = widget_configuration.properties.attributes
					.get(set_state.properties.state_group.entry)!
					.properties.type.cast("state group");
				state_configuration_property.root.transact(async () => {
					state_configuration_property.set_selected_state(set_state.properties.state.entry);
					(state_configuration_property.properties as Writeable<api_modification.Cstate_group["properties"]>).node =
						new api_modification.Cconfiguration_node(
							await mapInstructionArgumentToModification(
								set_state.properties.node,
								destructor,
								value,
								implementation_context
							),
							state_configuration_property,
							{
								widget_node: () => set_state.properties.state.ref.properties.node,
							},
							"node"
						);
				});
				storeState(widget_configuration.root);
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				setWidgetState(
					node_context,
					implementation_context.last_gui_widget_configuration,
					set_state.properties.state_group.ref,
					set_state.properties.state.entry,
					await mapInstructionArgumentToModification(
						set_state.properties.node,
						destructor,
						value,
						implementation_context
					),
					implementation_context.entry_keys
				);
			} else {
				unexpectedState(widget_configuration);
			}
			break;
		}
		case "set text": {
			const set_text = configuration_instruction.properties.instruction.state.node;
			const new_text_value = (await visitExpression(set_text.properties.argument.properties.expression).execute(
				argument_context,
				destructor,
				value
			)) as string;
			const widget_configuration = implementation_context.widget_configuration;
			if (widget_configuration instanceof api_views.Cgui_widget_configuration_node) {
				const text_configuration_property = widget_configuration.properties.configuration
					.get(set_text.properties.text.entry)
					?.properties.attribute.properties.type.cast("configuration")
					.properties.type.cast("text");
				if (text_configuration_property === undefined) {
					storeWidgetText(
						node_context,
						implementation_context.last_gui_widget_configuration,
						set_text.properties.text.ref,
						new_text_value,
						implementation_context.entry_keys
					);
				} else {
					storeText(node_context, text_configuration_property, new_text_value, implementation_context.entry_keys);
				}
			} else if (widget_configuration instanceof api_modification.Cconfiguration_node) {
				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const text_property = widget_configuration.properties.attributes
					.get(set_text.properties.text.entry)!
					.properties.type.cast("text");
				text_property.set_value(new_text_value);
			} else if (widget_configuration instanceof api_widget.Cwidget_configuration_node) {
				storeWidgetText(
					node_context,
					implementation_context.last_gui_widget_configuration,
					set_text.properties.text.ref,
					new_text_value,
					implementation_context.entry_keys
				);
			} else {
				unexpectedState(widget_configuration);
			}
			break;
		}
		default:
			unexpectedState(configuration_instruction.properties.instruction.state);
	}
}

async function mapInstructionArgumentToModification(
	node: api_widget.Cinstruction_argument_configuration_node,
	destructor: DestructorTree,
	value: EventValue,
	implementation_context: ImplementationVisitorContext
): Promise<api_modification.Tconfiguration_node> {
	const new_node: api_modification.Tconfiguration_node = {
		attributes: {},
	};
	for (const [key, attribute] of node.properties.attributes) {
		switch (attribute.properties.type.state.name) {
			case "configuration": {
				const config = attribute.properties.type.state.node;
				switch (config.properties.type.state.name) {
					case "number": {
						const $ = config.properties.type.state.node;
						new_node.attributes[key] = {
							type: [
								"number",
								{
									value: (await visitExpression($.properties.argument.properties.expression).execute(
										implementation_context,
										destructor,
										value
									)) as number,
								},
							],
						};
						break;
					}
					case "state group": {
						const $ = config.properties.type.state.node;
						new_node.attributes[key] = {
							type: [
								"state group",
								{
									"selected state": $.properties.state.entry,
									node: await mapInstructionArgumentToModification(
										$.properties.node,
										destructor,
										value,
										implementation_context
									),
								},
							],
						};
						break;
					}
					case "text": {
						const $ = config.properties.type.state.node;
						new_node.attributes[key] = {
							type: [
								"text",
								{
									value: (await visitExpression($.properties.argument.properties.expression).execute(
										implementation_context,
										destructor,
										value
									)) as string,
								},
							],
						};
						break;
					}
					default:
						unexpectedState(config.properties.type.state);
				}
				break;
			}
			default:
				unexpectedState(attribute.properties.type.state.name);
		}
	}
	return new_node;
}

export async function instruction_selection(
	instruction: api_widget.Cinstruction_selection,
	implementation_context: ImplementationVisitorContext,
	destructor: DestructorTree,
	value: string | number | undefined | File
): Promise<void> {
	try {
		const instruction_implementation_context = context_selection.context_selection(
			instruction.properties.context,
			implementation_context,
			destructor
		);
		switch (instruction.properties.configuration_attribute_type.state.name) {
			case "binding": {
				const binding_event = instruction.properties.configuration_attribute_type.state.node;
				await executeInstruction(
					binding_event.properties.instruction.entry,
					instruction_implementation_context,
					implementation_context,
					binding_event,
					destructor,
					value
				);
				break;
			}
			case "configuration": {
				const configuration_type = instruction.properties.configuration_attribute_type.state.node;
				await execute_configuration_intruction_handler(
					instruction_implementation_context,
					implementation_context,
					configuration_type,
					destructor,
					value
				);
				break;
			}
			default:
				return unexpectedState(instruction.properties.configuration_attribute_type.state);
		}
	} catch (error) {
		if (error instanceof Error && error.message.startsWith("Trying to access destroyed property!")) {
			//FIXME make destroyed property Errors typed e.g. DestroyedError
			console.warn("Aborting instruction because of destroyed property ", error);
			throw new InstructionError("Trying to access destroyed property!");
		}
		if (isNonDecoratedError(error)) {
			throw decorateInstructionError(error, instruction, implementation_context);
		}
		throw error;
	}
}
